import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const badgeVariants = cva(
  [
    "inline-flex items-center justify-center gap-1",
    "rounded-[var(--radius-full)] border border-transparent",
    "px-[var(--space-2-5)] py-[var(--space-1)]",
    "text-[length:var(--font-size-xs)] font-[number:var(--font-weight-medium)]",
    "whitespace-nowrap transition-colors",
    "[&>svg]:size-3 [&>svg]:shrink-0",
  ].join(" "),
  {
    variants: {
      variant: {
        default: "bg-[var(--color-primary)] text-[var(--color-primary-foreground)]",
        secondary: "bg-[var(--color-bg-muted)] text-[var(--color-text-secondary)]",
        outline: "border-[var(--color-border-default)] text-[var(--color-text-primary)]",
        success: "bg-[var(--color-success-subtle)] text-[var(--color-success)]",
        warning: "bg-[var(--color-warning-subtle)] text-[var(--color-warning)]",
        error: "bg-[var(--color-error-subtle)] text-[var(--color-error)]",
        info: "bg-[var(--color-info-subtle)] text-[var(--color-info)]",
        // Backward compat
        destructive: "bg-[var(--color-error-subtle)] text-[var(--color-error)]",
        ghost: "text-[var(--color-text-muted)]",
        link: "text-[var(--color-text-link)] underline-offset-4 hover:underline",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

interface BadgeProps
  extends React.ComponentProps<"span">,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant = "default", ...props }: BadgeProps) {
  return (
    <span
      data-slot="badge"
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  );
}

export { Badge, badgeVariants };
export type { BadgeProps };
